import { useState, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import SearchBar from "components/layout/SearchBar";
import FunctionalButton from "components/layout/FunctionalButton";
import MissionListView, { MissionListItem } from "./MissionListView";

type MissionListPanelProps = {
    missions: MissionListItem[];
    onSelect: (mission: MissionListItem) => void;
    isLoading?: boolean;
    isError?: boolean;
    readOnly?: boolean;
    selectedMissionKey?: string | null;
    className?: string;
    searchQuery?: string;
    onSearchQueryChange?: (value: string) => void;
    sortMode?: "createdAt" | "name";
    onSortModeChange?: (value: "createdAt" | "name") => void;
    toolbarContent?: ReactNode;
    activeMissionId?: string | null;
    isMissionActive?: boolean;
    isEditMode?: boolean;
    selectedIds?: string[];
    onToggleSelection?: (id: string) => void;
    onEnterEditMode?: () => void;
    isDeleting?: boolean;
    onEdit?: (missionId: string) => void;
    scrollToMissionId?: string | null;
    scrollWhenVisible?: boolean;
};

const MissionListPanel = ({
    missions,
    onSelect,
    isLoading = false,
    isError = false,
    readOnly = false,
    selectedMissionKey = null,
    className = "",
    searchQuery: controlledSearchQuery,
    onSearchQueryChange,
    sortMode: controlledSortMode,
    onSortModeChange,
    toolbarContent,
    activeMissionId,
    isMissionActive = false,
    isEditMode = false,
    selectedIds = [],
    onToggleSelection,
    onEnterEditMode,
    isDeleting = false,
    onEdit,
    scrollToMissionId,
    scrollWhenVisible = true,
}: MissionListPanelProps) => {
    const [internalSearchQuery, setInternalSearchQuery] = useState("");
    const [internalSortMode, setInternalSortMode] = useState<"createdAt" | "name">("createdAt");
    const searchQuery = controlledSearchQuery ?? internalSearchQuery;
    const sortMode = controlledSortMode ?? internalSortMode;

    const updateSearchQuery = (value: string) => {
        setInternalSearchQuery(value);
        onSearchQueryChange?.(value);
    };

    const toggleSortMode = () => {
        const nextSortMode = sortMode === "createdAt" ? "name" : "createdAt";
        setInternalSortMode(nextSortMode);
        onSortModeChange?.(nextSortMode);
    };

    return (
        <div
            className={twMerge(
                "flex h-full min-h-0 w-full flex-col rounded-r10 bg-black90 px-2 py-4",
                className
            )}
        >
            <div className="flex shrink-0 items-center gap-2 px-2">
                <SearchBar
                    className="min-w-0 flex-1"
                    value={searchQuery}
                    onChange={() => {
                        const input = document.activeElement;
                        updateSearchQuery(input instanceof HTMLInputElement ? input.value : "");
                    }}
                />
                {toolbarContent}
                {!toolbarContent && (
                    <FunctionalButton
                        title="Sort"
                        className="h-6 shrink-0 rounded-r3 hover:bg-black70"
                        onClick={toggleSortMode}
                    />
                )}
            </div>
            <div className="scroll-vertical mt-2 min-h-0 flex-1 overflow-y-auto px-2">
                <MissionListView
                    missions={missions}
                    onSelect={onSelect}
                    isLoading={isLoading}
                    isError={isError}
                    sortMode={sortMode}
                    searchQuery={searchQuery}
                    readOnly={readOnly}
                    activeMissionId={activeMissionId ?? selectedMissionKey}
                    isMissionActive={isMissionActive}
                    isEditMode={isEditMode}
                    selectedIds={selectedIds}
                    onToggleSelection={onToggleSelection}
                    onEnterEditMode={onEnterEditMode}
                    isDeleting={isDeleting}
                    onEdit={onEdit}
                    scrollToMissionId={scrollToMissionId ?? selectedMissionKey}
                    scrollWhenVisible={scrollWhenVisible}
                />
            </div>
        </div>
    );
};

export default MissionListPanel;
